import {
  Box,
  Button,
  Chip,
  DialogProps,
  Grid,
  IconButton,
  TablePagination,
  Tooltip,
  Typography,
} from '@mui/material'
import Label from 'components/Label'
import React, { FC, useCallback, useEffect, useState } from 'react'
import {
  TeenagerProcessFilterInit,
  TeenagerProcessFilterSchema,
  TeenagerProcessFilterType,
  TeenagerProcessType,
} from '../../../../types/teenager-process.type'
import { useNavigate, useParams } from 'react-router-dom'
import client from '../../../../services/client'
import { BaseData, BaseDataContent } from '../../../../types/base.type'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormInput from 'components/FormInput'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { cnjMask } from 'utils/mask.util'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { StyledCardProcess } from 'pages/TeenagersEdit/components/TabRelatedProcesses/style'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import { TeenagerProcessHearingType } from '../../../../types/teenager-process-hearing.type'
import {
  SocioeducationMeasureCreateResponseType,
  SocioeducationMeasureCreateSchema,
  SocioeducationMeasureCreateType,
} from 'types/socioeducation-measure-identification.type'
import { useCreasUsuario } from 'pages/SearchCreas/hook/creas-usuario.hook'
import { CreasResponse } from '../../../../types/creas.type'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { PageableProps } from '../../../../types/pageable.type'
import { useSnackBar } from '../../../../contexts/snackbar.context'
import { isCnj } from 'utils/validate.util'
import { HttpErrorType } from '../../../../types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'
import { usePagination } from 'hooks/pagination.hook'
import { useBackdrop } from '../../../../contexts/backdrop.context'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import { useAuthContext } from '../../../../contexts/auth.context'
import { medidaAplicadaOptions } from 'pages/SocioeducationMeasureEdit/ProceduralData'

export const TabRelatedProcesses: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { creas } = useCreasUsuario()
  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()
  const { handleHasPermission } = useAuthContext()

  const [processos, setProcessos] = useState<TeenagerProcessType[]>([])

  const [
    openDialogCadastrarMedidaSocioeducativaFull,
    setOpenDialogCadastrarMedidaSocioeducativaFull,
  ] = useState<boolean>(false)
  const [
    openDialogCadastrarMedidaSocioeducativaCreas,
    setOpenDialogCadastrarMedidaSocioeducativaCreas,
  ] = useState<boolean>(false)
  const [numeroCnj, setNumeroCnj] = useState<string | null>(null)
  const [rowsPerPage, setRowsPerPage] = useState(6)
  const {
    page,
    pages: { handleTotalOfPages },
    handleNewPage,
    elements: { totalOfElements, handleTotalOfElements },
  } = usePagination()

  const { control, handleSubmit, reset, setValue, watch } = useForm<TeenagerProcessFilterType>({
    resolver: zodResolver(TeenagerProcessFilterSchema),
    defaultValues: TeenagerProcessFilterInit,
  })

  const getMedidaAplicada = (medidaAplicada?: string | null) => {
    if (medidaAplicada) {
      return medidaAplicadaOptions.find((item) => item.value === medidaAplicada)?.label
    }

    return ''
  }

  const numeroCnjWatch = watch('numeroCnj')

  const checkIfExists = async (numeroCnj?: string): Promise<boolean> => {
    if (id) {
      return await buscarProcessos(id, { numeroCnj: numeroCnj }, { page: 0, pageSize: 1 })
        .then((response) => {
          if (id && response.data.totalElements > 0) {
            showSnackBar('Esse processo já existe!', 'warning')
            handleBuscarPageagle({ numeroCnj: numeroCnj }, { page: 0, pageSize: rowsPerPage })
            return true
          }
          return false
        })
        .catch(() => false)
    }
    return false
  }

  const handleBuscar = useCallback(
    async (data?: TeenagerProcessFilterType) => {
      if (id) {
        await handleBuscarPageagle(data, { page: page, pageSize: rowsPerPage })
      }
    },
    [id, page, rowsPerPage],
  )

  const handleBuscarPageagle = async (
    data?: TeenagerProcessFilterType,
    pageable?: PageableProps,
  ) => {
    if (id) {
      handleBackdropOpen()
      await buscarProcessos(id, data, pageable)
        .then((response) => {
          console.log('Consulta de processos!!')
          if (response.data) {
            console.log('Data: ', response.data)
            setProcessos(response.data.content)
            handleTotalOfElements(response.data.totalElements)
            handleTotalOfPages(response.data.totalPages)
          }
        })
        .finally(() => handleBackdropClose())
    }
  }

  const buscarProcessos = async (
    id: string,
    data?: TeenagerProcessFilterType,
    pageable?: PageableProps,
  ): Promise<BaseDataContent<TeenagerProcessType[]>> => {
    const url = `/adolescente/${id}/processos`
    return await client.get<BaseDataContent<TeenagerProcessType[]>>(url, {
      ...data,
      ...pageable,
    })
  }

  const cadastrarMeioAberto = async (data?: SocioeducationMeasureCreateType) => {
    setOpenDialogCadastrarMedidaSocioeducativaFull(false)
    setOpenDialogCadastrarMedidaSocioeducativaCreas(false)
    if (data && id) {
      const url = `/meio-aberto`
      handleBackdropOpen()
      await client
        .post<BaseData<SocioeducationMeasureCreateResponseType>>(url, {
          numeroCnj: data.numeroCnj,
          creasId: data.creasId,
          pessoaFisicaId: id,
        })
        .then(
          (response) => response.data && navigate(`/meio-aberto/${response.data.id}/identificacao`),
        )
        .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
        .finally(() => handleBackdropClose())
    }
  }

  const handlePage = async (newPage: number, newRowsPerPage?: any) => {
    handleNewPage(null, newPage)

    if (newRowsPerPage?.target?.value) {
      setRowsPerPage(newRowsPerPage?.target?.value)
    }
  }

  useEffect(() => {
    handleSubmit((data) => handleBuscar(data))()
  }, [handleBuscar])

  useEffect(() => {
    setValue('numeroCnj', cnjMask(numeroCnjWatch))
  }, [numeroCnjWatch])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Label text='Processos Relacionados' v='h6' color='primary' bold />
      </Grid>
      <Grid item xs={12}>
        <form onSubmit={handleSubmit(handleBuscar)}>
          <Grid container item xs={12} spacing={2}>
            <Grid item lg={6} xs={12}>
              <FormInput
                control={control}
                name={'numeroCnj'}
                label={'Número VIJ'}
                placeholder='Informe o número VIJ'
                maxLength={25}
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <FormInput
                control={control}
                name='numeroVemse'
                label='Número VEMSE'
                placeholder='Informe o número VEMSE'
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <FormInputAutoComplete
                label={'CREAS'}
                name={'creas'}
                control={control}
                options={creas}
                getOptionLabel={(option) => option.nome}
                getOptionById={(id: number | null, options: CreasResponse[]) =>
                  options.find((option) => option.id === id) || null
                }
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChangeValue={(option) => option?.id || null}
              />
            </Grid>
            <Grid item xs={12} display='flex' flexDirection={'row'} justifyContent='space-between'>
              <Box display={'flex'} gap={2}>
                <Button
                  variant={'outlined'}
                  color={'secondary'}
                  onClick={() => {
                    reset()
                    handleNewPage(null, 0)
                    setProcessos([])
                  }}
                >
                  Limpar
                </Button>
                <Button
                  startIcon={<SearchRoundedIcon />}
                  variant={'contained'}
                  color={'secondary'}
                  onClick={() => {
                    handleSubmit(async (data) => {
                      if (id) {
                        handleNewPage(null, 0)
                        await handleBuscarPageagle(data, { page: 0, pageSize: rowsPerPage })
                      }
                    })()
                  }}
                >
                  Pesquisar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Grid>
      <Grid item xs={12} />
      <Grid container item xs={12} spacing={2}>
        {processos.length > 0 && (
          <>
            {processos.map((processo, index) => (
              <Grid key={index} item xl={4} lg={6} md={12} xs={12}>
                <StyledCardProcess
                  display={'flex'}
                  flexDirection={'column'}
                  height={'100%'}
                  minHeight={'170px'}
                >
                  <Box
                    padding={'0.25rem'}
                    display={'flex'}
                    flexDirection={'column'}
                    height={'100%'}
                    gap={2}
                  >
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      minHeight={'34px'}
                    >
                      <Typography variant={'body1'} color={'primary'}>
                        <b>Número VIJ: {cnjMask(processo.numeroCnj)}</b>
                      </Typography>
                      {processo.processoIntegraPolicialId && (
                        <Tooltip title={'Visualizar Processo'}>
                          <IconButton
                            color={'primary'}
                            size={'small'}
                            onClick={() =>
                              navigate(`/processos-pendentes/${processo.processoIntegraPolicialId}`)
                            }
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {processo.meioAbertoId && (
                      <Box display={'flex'} flexDirection={'column'} gap={1}>
                        <Tooltip title={'Número do processo de execução.'} placement='bottom-start'>
                          <Typography variant={'body2'}>
                            Número VEMSE: {cnjMask(processo.numeroVemse)}
                          </Typography>
                        </Tooltip>
                        <Typography variant={'body2'}>CREAS: {processo.creas?.nome}</Typography>
                        <Typography variant={'body2'}>
                          Medida Socioeducativa Aplicada:{' '}
                          {getMedidaAplicada(processo.tipoMedidaSocioeducativa)}
                        </Typography>
                      </Box>
                    )}
                    {processo.meioAbertoId &&
                      handleHasPermission(Funcionalidades.meioAberto.visualizar) &&
                      processo.podeVisualizar && (
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          flexWrap={'wrap'}
                          marginBottom={'1rem'}
                          height={'100%'}
                          alignItems={'end'}
                          gap={1}
                        >
                          <Tooltip title={'Visualizar Medida Socioeducativa'}>
                            <Chip
                              label={
                                <Box display={'flex'} alignItems={'center'} gap={'0.25rem'}>
                                  <VisibilityIcon fontSize={'small'} />
                                  <span>Visualizar Medida Socioeducativa</span>
                                </Box>
                              }
                              size='small'
                              variant={'outlined'}
                              color={'info'}
                              onClick={() => {
                                navigate(`/meio-aberto/${processo.meioAbertoId}/identificacao`)
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                  </Box>
                </StyledCardProcess>
              </Grid>
            ))}
            <Grid item xs={12}>
              <TablePagination
                labelRowsPerPage='Linhas por página: '
                labelDisplayedRows={({ from, to, count }) => {
                  return `${from}–${to} de ${count}`
                }}
                rowsPerPageOptions={[3, 6, 9, 12, 15]}
                count={totalOfElements}
                rowsPerPage={rowsPerPage}
                onPageChange={(e, p) => handlePage(p)}
                page={page}
                component='div'
                onRowsPerPageChange={(e) => handlePage(0, e)}
              />
            </Grid>
          </>
        )}
      </Grid>
      <DialogCadastrarMedidaSocioeducativaFull
        creas={creas}
        open={openDialogCadastrarMedidaSocioeducativaFull}
        onClose={() => {
          setOpenDialogCadastrarMedidaSocioeducativaFull(false)
        }}
        onSave={cadastrarMeioAberto}
        onCheckIfExists={checkIfExists}
      />

      <DialogCadastrarMedidaSocioeducativaCreas
        numeroCnj={numeroCnj}
        creas={creas}
        open={openDialogCadastrarMedidaSocioeducativaCreas}
        onClose={() => {
          setOpenDialogCadastrarMedidaSocioeducativaCreas(false)
          setNumeroCnj(null)
        }}
        onSave={cadastrarMeioAberto}
      />
    </Grid>
  )
}

type DialogCadastrarMedidaSocioeducativaFullProps = {
  open: boolean
  creas: CreasResponse[]
  onSave: (data?: SocioeducationMeasureCreateType) => Promise<void>
  onClose: (indexAdolescente?: number, value?: TeenagerProcessHearingType) => void
  onCheckIfExists: (numeroCnj?: string) => Promise<boolean>
}

type DialogCadastrarMedidaSocioeducativaCreasProps = {
  open: boolean
  numeroCnj?: string | null
  creas: CreasResponse[]
  onSave: (data?: SocioeducationMeasureCreateType) => Promise<void>
  onClose: (indexAdolescente?: number, value?: TeenagerProcessHearingType) => void
}

const DialogCadastrarMedidaSocioeducativaFull: FC<DialogCadastrarMedidaSocioeducativaFullProps> = ({
  open,
  creas,
  onClose,
  onSave,
  onCheckIfExists,
}) => {
  const [fullWidth] = useState(true)
  const [maxWidth] = useState<DialogProps['maxWidth']>('sm')
  const [isFirstStep, setFirstStep] = useState<boolean>(true)

  const { control, handleSubmit, setValue, reset, watch } =
    useForm<SocioeducationMeasureCreateType>({
      resolver: zodResolver(SocioeducationMeasureCreateSchema),
      defaultValues: {},
    })

  const numeroCnjWatch = watch('numeroCnj')

  useEffect(() => {
    reset()
  }, [open])

  const handleClose = () => {
    onClose()
    setTimeout(() => setFirstStep(true), 300)
  }

  const handleSave = () => {
    handleSubmit((data) => {
      onSave(data)
      setTimeout(() => setFirstStep(true), 300)
    })()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      aria-labelledby='alert-dialog-title'
    >
      <DialogTitle variant={'body1'} color={'primary'}>
        <strong>{isFirstStep ? 'CADASTRAR MEDIDA SOCIOEDUCATIVA' : 'INFORMAR CREAS'}</strong>
      </DialogTitle>
      <DialogContent>
        <form>
          <Grid container spacing={'0.4rem'}>
            <Grid item xs={12} />
            {isFirstStep ? (
              <Grid item xs={12}>
                <FormInput
                  label={'Informe o número do Processo de Conhecimento (VIJ):'}
                  name={'numeroCnj'}
                  control={control}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <FormInputAutoComplete
                  label={'CREAS'}
                  name={'creasId'}
                  control={control}
                  options={creas}
                  getOptionLabel={(option) => option.nome}
                  getOptionById={(id: number | null, options: CreasResponse[]) =>
                    options.find((option) => option.id === id) || null
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onChangeValue={(option) => option?.id || null}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ paddingBottom: 2 }}>
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'center'}
          width={'100%'}
          gap={2}
        >
          <Button variant={'outlined'} color={'secondary'} onClick={() => handleClose()}>
            Voltar
          </Button>
          {isFirstStep ? (
            <Button
              variant={'contained'}
              color={'secondary'}
              onClick={() => {
                handleSubmit(() => {
                  if (numeroCnjWatch && isCnj(numeroCnjWatch)) {
                    onCheckIfExists(numeroCnjWatch).then((exist) => {
                      if (exist) {
                        handleClose()
                      } else {
                        setValue('numeroCnjIsValid', true)
                        setFirstStep(false)
                      }
                    })
                  }
                })()
              }}
            >
              Avancar
            </Button>
          ) : (
            <Button variant={'contained'} color={'secondary'} onClick={() => handleSave()}>
              Salvar
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}

const DialogCadastrarMedidaSocioeducativaCreas: FC<DialogCadastrarMedidaSocioeducativaCreasProps> =
  ({ open, creas, numeroCnj, onClose, onSave }) => {
    const [fullWidth] = useState(true)
    const [maxWidth] = useState<DialogProps['maxWidth']>('sm')

    const { control, handleSubmit, setValue, reset } = useForm<SocioeducationMeasureCreateType>({
      resolver: zodResolver(SocioeducationMeasureCreateSchema),
      defaultValues: {},
    })

    useEffect(() => {
      reset()
    }, [open])

    useEffect(() => {
      if (numeroCnj) {
        setValue('numeroCnj', numeroCnj)
        setValue('numeroCnjIsValid', true)
      }
    }, [numeroCnj])

    const handleClose = () => {
      onClose()
    }

    const handleSave = () => {
      handleSubmit((data) => {
        onSave(data)
      })()
    }

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        aria-labelledby='alert-dialog-title'
      >
        <DialogTitle variant={'body1'} color={'primary'}>
          <strong>INFORMAR CREAS</strong>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(handleSave)}>
            <Grid container spacing={'0.4rem'}>
              <Grid item xs={12} />
              <Grid item xs={12}>
                <FormInputAutoComplete
                  label={'CREAS'}
                  name={'creasId'}
                  control={control}
                  options={creas}
                  getOptionLabel={(option) => option.nome}
                  getOptionById={(id: number | null, options: CreasResponse[]) =>
                    options.find((option) => option.id === id) || null
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onChangeValue={(option) => option?.id || null}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ paddingBottom: 2 }}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'center'}
            width={'100%'}
            gap={2}
          >
            <Button variant={'outlined'} color={'secondary'} onClick={() => handleClose()}>
              Voltar
            </Button>
            <Button variant={'contained'} color={'secondary'} onClick={() => handleSave()}>
              Salvar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    )
  }
